import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BusinessTypeProvider, useBusinessType } from '@/contexts/BusinessTypeContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  LayoutDashboard, Users, Truck, Store, ShoppingBag, BarChart3,
  Settings, MessageSquare, Star, Tag, Menu, LogOut, ChevronDown, ChevronRight,
  UtensilsCrossed, DollarSign, MapPin, ClipboardList, UserCircle, Bell, X,
  Sparkles, Shield, Building2, Mail, FileText, MessageCircle, Key
} from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import ImpersonationBanner from '@/components/ImpersonationBanner';

// HubSpot-style categorized navigation for Admin
const adminNavCategories = [
  {
    label: 'Workspace',
    icon: LayoutDashboard,
    items: [
      { label: 'Overview', path: '/admin', icon: LayoutDashboard },
      { label: 'AI Analytics', path: '/admin/ai-analytics', icon: Sparkles },
    ]
  },
  {
    label: 'Operations',
    icon: ShoppingBag,
    items: [
      { label: 'Users', path: '/admin/users', icon: Users },
      { label: 'Vendors', path: '/admin/vendors', icon: Store },
      { label: 'Pending Vendors', path: '/admin/vendors/pending', icon: FileText },
      { label: 'Drivers', path: '/admin/drivers', icon: Truck },
      { label: 'Orders', path: '/admin/orders', icon: ShoppingBag },
    ]
  },
  {
    label: 'Support',
    icon: MessageSquare,
    items: [
      { label: 'Tickets', path: '/admin/tickets', icon: MessageSquare },
      { label: 'Reviews', path: '/admin/reviews', icon: Star },
      { label: 'Chat', path: '/admin/chat', icon: MessageCircle },
    ]
  },
  {
    label: 'Analytics',
    icon: BarChart3,
    items: [
      { label: 'Overview', path: '/admin/analytics', icon: BarChart3 },
      { label: 'Revenue & Predictions', path: '/admin/revenue-analytics', icon: DollarSign },
      { label: 'AI Insights', path: '/admin/ai-analytics', icon: Sparkles },
    ]
  },
  {
    label: 'Administration',
    icon: Settings,
    items: [
      { label: 'Settings', path: '/admin/settings', icon: Settings },
      { label: 'Integrations', path: '/admin/integrations', icon: Key },
      { label: 'Promo Codes', path: '/admin/promos', icon: Tag },
      { label: 'Security', path: '/admin/security', icon: Shield },
      { label: 'Document Compliance', path: '/admin/document-management', icon: FileText },
      { label: 'RBAC Management', path: '/admin/rbac', icon: Shield },
      { label: 'Enterprise', path: '/admin/enterprise-settings', icon: Building2 },
    ]
  },
];

// HubSpot-style categorized navigation for Vendor
// Dynamic vendor navigation based on business type
const getVendorNavCategories = (config) => [
  {
    label: 'Workspace',
    icon: LayoutDashboard,
    items: [
      { label: 'Overview', path: '/vendor', icon: LayoutDashboard },
    ]
  },
  {
    label: 'Operations',
    icon: ClipboardList,
    items: [
      { label: config.ordersLabel, path: '/vendor/orders', icon: ClipboardList },
      { label: config.catalogLabel, path: '/vendor/menu', icon: UtensilsCrossed },
      { label: 'Inventory', path: '/vendor/inventory', icon: Store },
      { label: 'Delivery Zones', path: '/vendor/zones', icon: MapPin },
    ]
  },
  {
    label: 'Business',
    icon: DollarSign,
    items: [
      { label: 'Analytics', path: '/vendor/analytics', icon: BarChart3 },
      { label: 'Earnings', path: '/vendor/earnings', icon: DollarSign },
      { label: 'Profile', path: '/vendor/profile', icon: UserCircle },
      { label: 'Reviews', path: '/vendor/reviews', icon: Star },
    ]
  },
];

function VendorNavigation({ children, mobileOpen, setMobileOpen }) {
  const { config } = useBusinessType();
  const vendorNavCategories = getVendorNavCategories(config);
  return <VendorDashboardShell navCategories={vendorNavCategories} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}>{children}</VendorDashboardShell>;
}

function CategorySection({ category, onItemClick }) {
  const [isOpen, setIsOpen] = useState(true);
  const location = useLocation();

  // Check if any item in this category is active
  const hasActiveItem = category.items.some(item => 
    location.pathname === item.path || 
    (item.path !== '/admin' && item.path !== '/vendor' && location.pathname.startsWith(item.path))
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-1">
      <CollapsibleTrigger className="w-full group">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors">
          <ChevronRight className={`h-3.5 w-3.5 transition-transform text-muted-foreground ${isOpen ? 'rotate-90' : ''}`} />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex-1 text-left">
            {category.label}
          </span>
          {hasActiveItem && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-0.5 pl-3">
        {category.items.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/admin' && item.path !== '/vendor' && location.pathname.startsWith(item.path));
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`sidebar-item flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'active bg-accent text-accent-foreground border-l-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/30'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

function SidebarContent({ navCategories, onItemClick }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role === 'admin' ? 'Admin' : 'Vendor';

  return (
    <div className="flex flex-col h-full bg-card border-r">
      {/* Brand */}
      <div className="p-4 pb-3">
        <div className="flex items-center gap-3">
          <img 
            src="/r2yn-logo-color.png" 
            alt="R2YN - Rushing 2 Your Needs" 
            className="h-10 w-auto object-contain"
            data-testid="sidebar-logo"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2 pl-1">{role} Dashboard</p>
      </div>
      <Separator />

      {/* Categorized Nav Items */}
      <ScrollArea className="flex-1 px-3 py-4">
        <div className="space-y-3">
          {navCategories.map((category) => (
            <CategorySection 
              key={category.label} 
              category={category} 
              onItemClick={onItemClick}
            />
          ))}
        </div>
      </ScrollArea>

      {/* User Section */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-xs font-semibold text-primary">{user?.name?.[0] || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-destructive"
          onClick={async () => { 
            await logout(); 
            navigate('/login'); 
          }}
          data-testid="sidebar-logout-button"
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign Out
        </Button>
      </div>
    </div>
  );
}

export default function DashboardShell({ children }) {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // For vendor users, wrap with BusinessTypeProvider for dynamic navigation
  if (user?.role === 'vendor') {
    return (
      <BusinessTypeProvider>
        <VendorNavigation mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}>
          {children}
        </VendorNavigation>
      </BusinessTypeProvider>
    );
  }
  
  // For admin users, use static navigation
  return (
    <div className="app-shell flex h-screen overflow-hidden bg-background">
      <aside className="hidden lg:flex w-64 shrink-0" data-testid="desktop-sidebar">
        <SidebarContent navCategories={adminNavCategories} onItemClick={() => {}} />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <SidebarContent navCategories={adminNavCategories} onItemClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b bg-card/95 backdrop-blur flex items-center px-4 md:px-6 gap-4 shrink-0">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-semibold text-primary">{user?.name?.[0] || 'U'}</span>
            </div>
          </div>
        </header>
        <ImpersonationBanner />
        <div className="flex-1 overflow-auto">
          <div className="w-full px-4 md:px-6 py-6 md:py-8">{children}</div>
        </div>
      </main>
    </div>
  );
}

function VendorDashboardShell({ children, mobileOpen, setMobileOpen }) {
  const { user } = useAuth();
  const { config } = useBusinessType();
  const vendorNavCategories = getVendorNavCategories(config);

  return (
    <div className="app-shell flex h-screen overflow-hidden bg-background">
      <aside className="hidden lg:flex w-64 shrink-0" data-testid="desktop-sidebar">
        <SidebarContent navCategories={vendorNavCategories} onItemClick={() => {}} />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <SidebarContent navCategories={vendorNavCategories} onItemClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b bg-card/95 backdrop-blur flex items-center px-4 md:px-6 gap-4 shrink-0">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-semibold text-primary">{user?.name?.[0] || 'U'}</span>
            </div>
          </div>
        </header>
        <ImpersonationBanner />
        <div className="flex-1 overflow-auto">
          <div className="w-full px-4 md:px-6 py-6 md:py-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
